/* Editorial Tenant Home — match allure-image.com structure exactly.

   Top→bottom flow (mirrors the live reference):
     1. HERO            — FULL-BLEED dark portrait, overlaid text + dual CTAs
                          (white solid "BOOK A SESSION" + outline "VIEW PACKAGES").
                          Italic gold accent on the last word of the headline.
                          Orange-gold hairline at the bottom of the section.
     2. THE STUDIO      — black canvas, asymmetric 2-col: eyebrow + gold stub
                          rule on the LEFT, big serif H2 + body on the RIGHT,
                          then a thin top rule + stats row LEFT-aligned
                          (Packages · Happy clients · Average rating).
     3. PHOTOSHOOT PACKAGES — black canvas, "SIGNATURE SESSIONS" eyebrow LEFT,
                          big H2 "Photoshoot packages" LEFT, "SEE ALL →" RIGHT,
                          then 3-column tall image cards (no chrome — just the
                          image, then small text underneath: name + price + body
                          + BOOK NOW link).
     4. HOW IT WORKS    — black canvas, asymmetric: eyebrow + H2 + body LEFT,
                          3 columns RIGHT with small framed numbered cards
                          (01/02/03 in gold-bordered box) + title + body.
                          Thin gold rule + "START NOW →" RIGHT.
     5. TESTIMONIAL     — slightly warmer dark, "LOVED BY OUR CLIENTS" eyebrow
                          LEFT, big italic H2 "It felt like art, not a
                          photoshoot." LEFT, "AVERAGE RATING / 5.0 ★★★★★" RIGHT,
                          then a single dark review card below.
     6. CLOSING CTA     — cream/light contrast: "READY WHEN YOU ARE" eyebrow +
                          big H2 + dual CTA. */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { EditorialSkeleton } from "./_EditorialSkeleton";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1532073150508-0c1df022bdd1?auto=format&fit=crop&w=2400&q=80";

function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.08 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}
function Reveal({ children, delay = 0, className = "" }) {
  const { ref, shown } = useReveal();
  return (
    <div ref={ref} className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 1s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 1s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}>
      {children}
    </div>
  );
}

function Eyebrow({ children, className = "" }) {
  return (
    <div className={`text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#D17C1F)] ${className}`}
         style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
      {children}
    </div>
  );
}

function startingPrice(svc) {
  if (Array.isArray(svc?.tiers) && svc.tiers.length > 0) {
    const ps = svc.tiers.map((t) => Number(t?.price)).filter((n) => Number.isFinite(n) && n > 0);
    if (ps.length) return Math.min(...ps);
  }
  const flat = svc?.price_from ?? svc?.starting_price ?? svc?.price;
  const n = Number(flat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* Split a 2-line headline so the LAST WORD of line 2 is wrapped in an italic
   gold span (matches "Captured with *intent*."). Falls back gracefully if
   the line is a single word. */
function splitItalicLast(line) {
  if (!line) return { lead: "", italic: "" };
  const idx = line.lastIndexOf(" ");
  if (idx < 0) return { lead: "", italic: line };
  // Preserve any trailing punctuation on the italic word.
  return { lead: line.slice(0, idx + 1), italic: line.slice(idx + 1) };
}

export default function EditorialTenantHome() {
  const { settings, loading } = useSiteSettings();

  const studioName = settings?.studio_name || "Studio";
  const heroEyebrow = settings?.hero_eyebrow || settings?.studio_location || "Studio Photography";
  const heroTitle1 = settings?.hero_title_line1 || "Light, shape, presence.";
  const heroTitle2 = settings?.hero_title_line2 || "Captured with intent.";
  const heroDesc = settings?.hero_description || settings?.tagline
    || `${studioName} is an editorial photography studio crafting refined portraits, brand stories, and life's milestones.`;
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK_HERO;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;

  const aboutEyebrow = settings?.about_eyebrow || "The Studio";
  const aboutTitle = settings?.about_title
    || "A modern photography studio for portraits, brands, and the moments in between.";
  const aboutBody = settings?.about_body
    || "From maternity and family portraits to glamour, graduation, and brand campaigns — every session is designed around you.";

  const [services, setServices] = useState([]);
  const [testimonial, setTestimonial] = useState(null);
  const [testimonialsCount, setTestimonialsCount] = useState(0);
  const [avgRating, setAvgRating] = useState(null);
  const [stats, setStats] = useState({ packages: null });
  useEffect(() => {
    api.get("/services").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setServices(list);
      setStats((s) => ({ ...s, packages: list.length }));
    }).catch(() => {});
    api.get("/testimonials").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setTestimonialsCount(list.length);
      if (list.length) {
        setTestimonial(list[0]);
        // Average rating computed from real testimonials. We only render
        // it as a stat tile / "5.0 ★★★★★" header when there ARE
        // testimonials — empty studios (Skenzy at launch) get the tile
        // suppressed instead of inheriting a fake "5.0" inherited from
        // the editorial-template defaults that previously read as a
        // brand leak from Allure Image.
        const ratings = list
          .map((t) => Number(t.rating ?? t.stars ?? t.score))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (ratings.length) {
          const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
          setAvgRating(Number(avg.toFixed(1)));
        }
      }
    }).catch(() => {});
  }, []);

  // SEO
  useEffect(() => {
    document.title = `${studioName} — Editorial Photography Studio`;
    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", heroDesc);
  }, [studioName, heroDesc]);

  const featured = services.slice(0, 3); // allure-image.com shows 3 across, not 4
  const STEPS = [
    { n: "01", title: "Choose your package",
      body: "Browse signature sessions and select the tier that fits your vision and budget." },
    { n: "02", title: "Reserve your date",
      body: "Pick your perfect date and time. Secure your slot with a small Zelle deposit." },
    { n: "03", title: "Step in front of the lens",
      body: "Arrive, relax, and let us craft images you'll treasure for a lifetime." },
  ];

  const line2Split = splitItalicLast(heroTitle2);

  // First-time visitors (no in-memory + no localStorage cache) see the
  // editorial skeleton for the ~300-800ms /api/site fetch window instead of
  // misleading fallback content ("Studio", "Light, shape, presence.", a
  // stock Unsplash hero). Returning visitors hydrate from localStorage and
  // skip this branch entirely. Placed AFTER all hooks to respect Rules of
  // Hooks — early-returning before useState/useEffect would crash on the
  // settings-arrives re-render.
  if (loading && !settings) {
    return <EditorialSkeleton eyebrow="Loading the studio." />;
  }

  return (
    <div data-testid="editorial-tenant-home" style={{ background: "var(--tenant-bg, #0A0908)", color: "var(--tenant-fg, #F5F2EC)" }}>

      {/* ── 1. HERO — FULL-BLEED dark portrait with overlaid text ──────── */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden" data-testid="editorial-hero">
        {/* Hero image */}
        <img src={optimizeImageUrl(heroImg, { w: 2400 })}
          srcSet={optimizedSrcSet(heroImg, [800, 1200, 1600, 2000, 2400]) || undefined}
          sizes="100vw"
          alt={`${studioName} hero`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: heroPos }} loading="eager" fetchpriority="high" decoding="async" />
        {/* Dark gradient overlay so text is legible regardless of photo content.
            Uses the tenant canvas color as the wash — dark theme → black wash,
            light theme → cream wash (text inverts via --tenant-fg). */}
        <div className="absolute inset-0" style={{ background: "var(--tenant-hero-overlay, linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.85)))" }} aria-hidden />
        {/* Vignette on left side so the eyebrow / headline / paragraph stay legible */}
        <div className="absolute inset-y-0 left-0 w-1/2" style={{ background: "var(--tenant-hero-vignette, linear-gradient(to right, rgba(0,0,0,0.55), transparent 50%))" }} aria-hidden />

        {/* Content positioned bottom-left like the reference */}
        <div className="relative z-10 w-full max-w-[1480px] mx-auto px-6 lg:px-12 pb-20 lg:pb-28 pt-32">
          <Reveal>
            <Eyebrow className="mb-5 text-[color:var(--tenant-fg,#F5F2EC)]/85">{heroEyebrow}</Eyebrow>
            <h1 className="leading-[0.92] tracking-tight max-w-5xl"
              style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(3.25rem, 8vw, 7.5rem)" }}>
              {heroTitle1}
              <span className="block">
                {line2Split.lead}
                <em className="italic text-[color:var(--tenant-accent,#D17C1F)] not-italic-fallback" style={{ fontStyle: "italic" }}>{line2Split.italic}</em>
              </span>
            </h1>
            <p className="mt-9 max-w-md text-sm sm:text-base leading-relaxed text-[color:var(--tenant-fg,#F5F2EC)]/90"
               style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
              {heroDesc}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={tenantHref("/booking")} data-testid="editorial-hero-book"
                className="inline-flex items-center gap-3 px-7 h-12 text-[10px] uppercase tracking-[0.38em] bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)] hover:bg-[color:var(--tenant-accent,#D17C1F)] transition-colors">
                Book a Session <span>→</span>
              </Link>
              <Link to={tenantHref("/services")} data-testid="editorial-hero-packages"
                className="inline-flex items-center gap-3 px-7 h-12 text-[10px] uppercase tracking-[0.38em] border border-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-fg,#F5F2EC)] hover:bg-[color:var(--tenant-fg,#F5F2EC)] hover:text-[color:var(--tenant-bg,#0A0908)] transition-colors">
                View Packages
              </Link>
            </div>
          </Reveal>
        </div>
        {/* Orange hairline at the bottom of the hero */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-[color:var(--tenant-accent,#D17C1F)]" aria-hidden />
      </section>

      {/* ── 2. THE STUDIO — asymmetric 2-col on black ────────────────── */}
      <section className="py-28 lg:py-36 px-6 lg:px-12" data-testid="editorial-about">
        <div className="max-w-[1240px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 lg:gap-20">
          {/* Left rail: eyebrow + accent stub rule */}
          <div>
            <Reveal>
              <Eyebrow>{aboutEyebrow}</Eyebrow>
              <div className="mt-6 h-px w-10 bg-[color:var(--tenant-accent,#D17C1F)]" aria-hidden />
            </Reveal>
          </div>
          {/* Right: title + body + stats */}
          <div>
            <Reveal delay={0.05}>
              <h2 className="font-serif leading-[1.05] tracking-tight"
                style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)" }}>
                {aboutTitle}
              </h2>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[color:var(--tenant-fg,#F5F2EC)]/75 max-w-2xl"
                 style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                {aboutBody}
              </p>
            </Reveal>
            {/* Hairline + stats row, left-aligned. Each tile now appears
                ONLY when its backing data is real — Skenzy Studio with 0
                packages + 0 testimonials previously inherited fake "200+
                HAPPY CLIENTS / 5★ AVERAGE RATING" from the template
                defaults, which read as a brand leak from Allure Image.
                If every tile is empty we suppress the row entirely
                rather than rendering a lonely hairline. */}
            {(() => {
              const tiles = [];
              if (stats.packages != null && stats.packages > 0) {
                tiles.push({ v: `${stats.packages}+`, l: "Packages" });
              }
              if (testimonialsCount > 0) {
                tiles.push({ v: `${testimonialsCount}+`, l: "Happy clients" });
              }
              if (avgRating != null) {
                tiles.push({ v: `${avgRating}★`, l: "Average rating" });
              }
              if (!tiles.length) return null;
              return (
                <Reveal delay={0.15}>
                  <div className="mt-16 pt-10 border-t border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] flex flex-wrap gap-12 lg:gap-20">
                    {tiles.map((s, i) => (
                      <div key={i} data-testid={`editorial-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="leading-none"
                             style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                          {s.v}
                        </div>
                        <div className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/55"
                             style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ── 3. PHOTOSHOOT PACKAGES — 3-col tall image cards ────────── */}
      {featured.length > 0 && (
        <section className="py-24 lg:py-32 px-6 lg:px-12" data-testid="editorial-packages">
          <div className="max-w-[1480px] mx-auto">
            <Reveal>
              <div className="flex items-end justify-between gap-6 flex-wrap mb-16">
                <div>
                  <Eyebrow>Signature Sessions</Eyebrow>
                  <h2 className="mt-5 font-serif leading-[1.05] tracking-tight"
                    style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)" }}>
                    Photoshoot packages
                  </h2>
                </div>
                <Link to={tenantHref("/services")} data-testid="editorial-packages-see-all"
                  className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)] hover:text-[color:var(--tenant-accent,#D17C1F)] transition-colors">
                  See all →
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {featured.map((s, i) => {
                const p = startingPrice(s);
                const slugOrId = s.slug || s.id;
                return (
                  <Reveal key={s.id || i} delay={0.05 * i}>
                    <Link to={tenantHref(`/booking?service=${slugOrId}`)} data-testid={`editorial-package-${i}`}
                      className="group block">
                      {/* Tall image — no chrome, no border, just the photo */}
                      <div className="aspect-[3/4] bg-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))] overflow-hidden">
                        {s.image_url ? (
                          <img src={optimizeImageUrl(s.image_url, { w: 1000 })}
                            srcSet={optimizedSrcSet(s.image_url, [500, 800, 1000, 1400]) || undefined}
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            alt={s.name}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                            loading="lazy" decoding="async" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[color:var(--tenant-fg,#F5F2EC)]/30 text-[10px] uppercase tracking-[0.32em]">{s.name}</div>
                        )}
                      </div>
                      {/* Below image: name + price + body + book now */}
                      <div className="mt-6">
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="font-serif text-2xl leading-tight"
                               style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{s.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/65 shrink-0"
                               style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                            {p != null ? `from $${p}` : "by request"}
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-[color:var(--tenant-fg,#F5F2EC)]/65 leading-relaxed line-clamp-2"
                           style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                          {s.description}
                        </p>
                        <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)] group-hover:opacity-80">
                          Book now →
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── 4. HOW IT WORKS — asymmetric: left title, right framed numbers ── */}
      <section className="py-28 lg:py-36 px-6 lg:px-12" data-testid="editorial-process">
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow>How it works</Eyebrow>
              <h2 className="mt-7 font-serif leading-[1.05] tracking-tight"
                style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)" }}>
                A simple, considered <em className="italic text-[color:var(--tenant-accent,#D17C1F)]">process.</em>
              </h2>
              <p className="mt-7 text-base sm:text-lg leading-relaxed text-[color:var(--tenant-fg,#F5F2EC)]/70 max-w-md"
                 style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                From first inquiry to delivered gallery — clear steps, zero guesswork.
              </p>
            </Reveal>
          </div>
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
              {STEPS.map((step, i) => (
                <Reveal key={step.n} delay={0.08 * i}>
                  {/* Small gold-bordered number card */}
                  <div className="w-12 h-14 border border-[color:var(--tenant-accent,#D17C1F)] flex items-center justify-center mb-7">
                    <span className="text-[color:var(--tenant-accent,#D17C1F)] italic text-base"
                          style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{step.n}</span>
                  </div>
                  <h3 className="font-serif text-xl leading-tight"
                      style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[color:var(--tenant-fg,#F5F2EC)]/65"
                     style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{step.body}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.35}>
              <div className="mt-14 pt-7 border-t border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] flex items-center justify-end">
                <Link to={tenantHref("/booking")} data-testid="editorial-process-cta"
                  className="text-[10px] uppercase tracking-[0.38em] text-[color:var(--tenant-fg,#F5F2EC)] hover:text-[color:var(--tenant-accent,#D17C1F)] transition-colors">
                  Start now →
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 5. LOVED BY OUR CLIENTS — testimonial ──────────────────────
          Renders ONLY when the studio has at least one real testimonial.
          Empty studios (Skenzy at launch) previously inherited a
          hard-coded "It felt like art, not a photoshoot." headline +
          a fake "5.0 ★★★★★" rating block from the template defaults —
          both read as a brand leak from Allure Image. Hiding the
          section is the honest representation until the studio's own
          testimonials are added. */}
      {testimonial && (
      <section className="py-28 lg:py-36 px-6 lg:px-12 bg-[color:var(--tenant-muted,#15100A)]" data-testid="editorial-testimonial">
        <div className="max-w-[1480px] mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-10 items-end mb-14">
              <div>
                <Eyebrow>Loved by our clients</Eyebrow>
                {/* Headline = the actual first testimonial's quote, italicised.
                    We strip any leading/trailing curly quotes the studio
                    pasted in so the heading reads cleanly under the
                    typeset &ldquo; / &rdquo; we wrap it with below. */}
                <h2 className="mt-7 font-serif leading-[1.05] tracking-tight"
                  style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)" }}>
                  &ldquo;<em className="italic text-[color:var(--tenant-accent,#D17C1F)]">
                    {(testimonial.quote || testimonial.message || testimonial.body || "")
                      .replace(/^[\s"“”]+|[\s"“”]+$/g, "")}
                  </em>&rdquo;
                </h2>
              </div>
              {/* Average rating tile — only renders when we have at least
                  one numeric rating in the testimonials list. Otherwise
                  the right column is omitted entirely (grid collapses to
                  the quote on its own). */}
              {avgRating != null && (
                <div className="lg:text-right" data-testid="editorial-testimonial-rating">
                  <Eyebrow>Average rating</Eyebrow>
                  <div className="mt-5 flex items-center lg:justify-end gap-3">
                    <span className="text-4xl"
                          style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>
                      {avgRating.toFixed ? avgRating.toFixed(1) : avgRating}
                    </span>
                    <span className="text-[color:var(--tenant-accent,#D17C1F)] tracking-widest text-lg">
                      {"★".repeat(Math.round(avgRating))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[color:var(--tenant-muted,#0E0B07)] border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))] p-8 lg:p-10 max-w-3xl">
              {Number(testimonial.rating ?? testimonial.stars ?? testimonial.score) > 0 && (
                <div className="text-[color:var(--tenant-accent,#D17C1F)] tracking-widest text-sm mb-5">
                  {"★".repeat(Math.round(Number(testimonial.rating ?? testimonial.stars ?? testimonial.score)))}
                </div>
              )}
              <blockquote className="border-l-2 border-[color:var(--tenant-accent,#D17C1F)] pl-5">
                <p className="text-xl italic leading-relaxed"
                   style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>
                  &ldquo;{testimonial.quote || testimonial.message || testimonial.body || "Lovely session!"}&rdquo;
                </p>
              </blockquote>
              <div className="mt-6">
                <div className="text-sm font-medium" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                  {testimonial.author || testimonial.name || "Anonymous"}
                </div>
                {testimonial.service && (
                  <div className="text-xs text-[color:var(--tenant-fg,#F5F2EC)]/55 mt-1" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
                    {testimonial.service}
                  </div>
                  )}
                </div>
              </div>
            </Reveal>
        </div>
      </section>
      )}

      {/* ── 6. CLOSING CTA — light cream contrast ──────────────────── */}
      <section className="py-32 lg:py-40 px-6 lg:px-12 text-center bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)]" data-testid="editorial-closing">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#D17C1F)]"
               style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
            Ready when you are
          </div>
          <h2 className="mt-7 font-serif leading-[1.04] tracking-tight max-w-4xl mx-auto"
            style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}>
            Let&rsquo;s create something <em className="italic text-[color:var(--tenant-accent,#D17C1F)]">timeless</em>.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to={tenantHref("/booking")} data-testid="editorial-closing-book"
              className="inline-flex items-center gap-3 px-8 h-12 text-[10px] uppercase tracking-[0.38em] bg-[color:var(--tenant-bg,#0A0908)] text-[color:var(--tenant-fg,#F5F2EC)] hover:bg-[color:var(--tenant-accent,#D17C1F)] transition-colors">
              Book a Session <span>→</span>
            </Link>
            <Link to={tenantHref("/contact")} data-testid="editorial-closing-contact"
              className="inline-flex items-center gap-3 px-8 h-12 text-[10px] uppercase tracking-[0.38em] border border-[color:var(--tenant-bg,#0A0908)] text-[color:var(--tenant-bg,#0A0908)] hover:bg-[color:var(--tenant-bg,#0A0908)] hover:text-[color:var(--tenant-fg,#F5F2EC)] transition-colors">
              Or send us a message
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
